import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ZCDButton } from '@zencity/common-ui';
import { useAtom } from 'jotai';
import { getImageDimensions } from '../../helper';
import { mapCamerasAtom } from '../../store';
import ImageReportMap from '../ImageReportMap';
import EmptyResults from '../EmptyResults';
import PinDropDataTables from '../GeolocationQuestionResults/PinDropDataTable';

// eslint-disable-next-line max-lines-per-function, max-statements
function ImageQuestionResults(props) {
  const { question, hasResponses } = props;
  const { data, showDataTable } = question;
  const { coordinates, imageUrl, inputs, pinOptionsCommentStatistic, pinOptionsSentimentCounts } =
    data;
  const [mapCameras, setMapCameras] = useAtom(mapCamerasAtom);
  const [mapData, setMapData] = useState(null);
  const [map, setMap] = useState(null);
  const savedCamera = useRef(null);
  const [selectedCoordinate, setSelectedCoordinate] = useState(null);
  const isEmpty = coordinates.length === 0;

  useEffect(() => {
    if (isEmpty) return;

    const padding = 500;
    getImageDimensions(imageUrl).then(({ height, width }) => {
      setMapData({
        center: [height / 2, width / 2],
        bounds: [
          [0, 0],
          [height, width],
        ],
        maxBounds: [
          [-padding, -padding],
          [height + padding, width + padding],
        ],
      });
    });
  }, [isEmpty]);

  const onMapFit = (instance) => {
    const camera = mapCameras[question.uuid];
    if (camera) instance.setView(camera.center, camera.zoom, { animate: false });

    const persistCamera = () => {
      setMapCameras((cameras) => ({
        ...cameras,
        [question.uuid]: {
          center: [instance.getCenter().lat, instance.getCenter().lng],
          zoom: instance.getZoom(),
        },
      }));
    };

    instance.on('moveend', persistCamera);
    instance.on('zoomend', persistCamera);
  };

  useEffect(() => {
    if (map && selectedCoordinate) map.flyTo([selectedCoordinate.y, selectedCoordinate.x], 2);
  }, [map, selectedCoordinate]);

  function renderContent() {
    if (isEmpty)
      return (
        <div style={{ padding: '2rem' }}>
          <EmptyResults hasResponses={hasResponses} />
        </div>
      );

    if (!mapData) return null;

    return (
      <>
        <div className="engage-results-image">
          <ImageReportMap
            coordinates={coordinates}
            imageUrl={imageUrl}
            center={mapData.center}
            bounds={mapData.bounds}
            maxBounds={mapData.maxBounds}
            onMapReady={setMap}
            onMapFit={onMapFit}
            inputs={inputs}
          />
          {selectedCoordinate && (
            <div className="engage-results-image__return">
              <ZCDButton
                text="Return"
                variant="secondary"
                preIcon="undo"
                onClick={() => {
                  map?.flyTo(savedCamera.current.center, savedCamera.current.zoom);
                  savedCamera.current = null;
                  setSelectedCoordinate(null);
                }}
              />
            </div>
          )}
        </div>

        {showDataTable && (
          <div>
            <PinDropDataTables
              inputs={inputs}
              pinOptionsCommentStatistic={pinOptionsCommentStatistic}
              pinOptionsSentimentCounts={pinOptionsSentimentCounts}
            />
          </div>
        )}
      </>
    );
  }

  return <div>{renderContent()}</div>;
}

ImageQuestionResults.propTypes = {
  question: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    chartType: PropTypes.string,
    showDataTable: PropTypes.bool,
    inputs: PropTypes.arrayOf(PropTypes.shape({})),
    data: PropTypes.shape({
      coordinates: PropTypes.arrayOf(
        PropTypes.shape({
          x: PropTypes.number.isRequired,
          y: PropTypes.number.isRequired,
        })
      ).isRequired,
      imageUrl: PropTypes.string.isRequired,
      inputs: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
          metadata: PropTypes.shape({
            allowComment: PropTypes.bool,
          }),
        })
      ),
      pinOptionsCommentStatistic: PropTypes.arrayOf(
        PropTypes.shape({
          ndoc: PropTypes.number,
          nentry: PropTypes.number,
          word: PropTypes.string,
        })
      ),
      pinOptionsSentimentCounts: PropTypes.arrayOf(
        PropTypes.shape({
          inputId: PropTypes.string,
          label: PropTypes.string,
          subtotal: PropTypes.number,
          total: PropTypes.number,
        })
      ),
    }).isRequired,
  }).isRequired,
  hasResponses: PropTypes.bool.isRequired,
};

export default ImageQuestionResults;
